"use client";

import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useState } from "react";

const fitMap = {
  contain: Fit.Contain,
  cover: Fit.Cover,
  fill: Fit.Fill,
  fitWidth: Fit.FitWidth,
  fitHeight: Fit.FitHeight,
  none: Fit.None,
  scaleDown: Fit.ScaleDown,
} as const;

const alignmentMap = {
  center: Alignment.Center,
  topCenter: Alignment.TopCenter,
  topLeft: Alignment.TopLeft,
  topRight: Alignment.TopRight,
  centerLeft: Alignment.CenterLeft,
  centerRight: Alignment.CenterRight,
  bottomCenter: Alignment.BottomCenter,
  bottomLeft: Alignment.BottomLeft,
  bottomRight: Alignment.BottomRight,
} as const;

export interface RiveAnimationProps {
  className?: string;
  src: string;
  /**
   * Optional layout fit mode. If omitted, runtime defaults are preserved for backward compatibility.
   */
  layoutFit?: keyof typeof fitMap;
  /**
   * Optional layout alignment. Only applied when layoutFit is provided.
   */
  layoutAlignment?: keyof typeof alignmentMap;
}

export function RiveAnimation({ src, className, layoutFit, layoutAlignment = "center" }: RiveAnimationProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  const { RiveComponent } = useRive({
    src,
    autoplay: true,
    layout: layoutFit
      ? new Layout({
          fit: fitMap[layoutFit],
          alignment: alignmentMap[layoutAlignment],
        })
      : undefined,
    onLoad: () => setIsLoaded(true),
  });

  return (
    <m.div
      animate={{ opacity: isLoaded ? 1 : 0 }}
      className={cn("h-full w-full", className)}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <RiveComponent className='h-full w-full' />
    </m.div>
  );
}
